/* global BigInt */
// Browser-owned calculation port; parity fixtures retain the original domain rules.

const CALCULATION_VERSION = 'energy-read-model-v1';
const DEFAULT_MAX_AGE_SECONDS = 10;
const DEFAULT_MAX_SKEW_SECONDS = 10;
const DEFAULT_MAX_SEGMENT_SECONDS = 15;

function finite(value) {
  return typeof value === 'number' && Number.isFinite(value);
}

function round(value, places = 9) {
  if (!finite(value)) return null;
  const factor = 10 ** places;
  return Math.round((value + Number.EPSILON) * factor) / factor;
}

function result(value, unit, status = 'complete', reasonCodes = [], details = {}) {
  return { value: value === undefined ? null : value, unit, status, reasonCodes, ...details };
}

function unavailable(unit, ...reasonCodes) {
  return result(null, unit, 'unavailable', reasonCodes.flat().filter(Boolean));
}

function notApplicable(unit, ...reasonCodes) {
  return result(null, unit, 'notApplicable', reasonCodes.flat().filter(Boolean));
}

function coverage(expectedSeconds, coveredSeconds, sampleCount = 0) {
  const expected = Math.max(Number(expectedSeconds) || 0, 0);
  const covered = Math.min(Math.max(Number(coveredSeconds) || 0, 0), expected);
  return {
    sampleCount: Math.max(Number(sampleCount) || 0, 0),
    expectedSeconds: round(expected, 3),
    coveredSeconds: round(covered, 3),
    missingSeconds: round(expected - covered, 3),
    coveragePct: expected === 0 ? null : round(100 * covered / expected, 6)
  };
}

function toTime(value) {
  const time = value instanceof Date ? value.getTime() : Date.parse(value);
  return Number.isFinite(time) ? time : null;
}

/** Select one non-future observation per role and report age/skew explicitly. */
function alignObservations(inputs, boundary, options = {}) {
  const boundaryMs = toTime(boundary);
  if (boundaryMs === null) throw new TypeError('A valid UTC alignment boundary is required');
  const maxAgeMs = (options.maxAgeSeconds ?? DEFAULT_MAX_AGE_SECONDS) * 1000;
  const maxSkewMs = (options.maxSkewSeconds ?? DEFAULT_MAX_SKEW_SECONDS) * 1000;
  const selected = {};
  const reasonCodes = [];
  const requiredRoles = options.requiredRoles || Object.keys(inputs || {});

  for (const role of requiredRoles) {
    const candidates = Array.isArray(inputs?.[role]) ? inputs[role] :
      (inputs?.[role] ? [inputs[role]] : []);
    const valid = candidates
      .map(item => ({ item, time: toTime(item.sampleTime) }))
      .filter(candidate => candidate.time !== null && candidate.time <= boundaryMs)
      .sort((a, b) => b.time - a.time);
    if (valid.length === 0) {
      selected[role] = null;
      reasonCodes.push(`missing:${role}`);
      continue;
    }
    const chosen = valid[0];
    const ageSeconds = (boundaryMs - chosen.time) / 1000;
    selected[role] = {
      ...chosen.item,
      sampleTime: new Date(chosen.time).toISOString(),
      ageSeconds: round(ageSeconds, 3),
      stale: ageSeconds * 1000 > maxAgeMs
    };
    if (selected[role].stale) reasonCodes.push(`stale:${role}`);
  }

  const usableTimes = Object.values(selected)
    .filter(item => item && !item.stale)
    .map(item => toTime(item.sampleTime));
  const missing = requiredRoles.some(role => !selected[role] || selected[role].stale);
  const spreadSeconds = usableTimes.length < 2 ? 0 :
    (Math.max(...usableTimes) - Math.min(...usableTimes)) / 1000;
  const misaligned = !missing && spreadSeconds * 1000 > maxSkewMs;
  if (misaligned) reasonCodes.push('inputSkewExceeded');
  return {
    boundary: new Date(boundaryMs).toISOString(),
    alignmentStatus: missing ? 'incomplete' : (misaligned ? 'misaligned' : 'aligned'),
    spreadSeconds: round(spreadSeconds, 3),
    maximumAgeSeconds: maxAgeMs / 1000,
    maximumSkewSeconds: maxSkewMs / 1000,
    reasonCodes,
    inputs: selected
  };
}

function calculateActivePowerBalance(input) {
  const required = ['generationKw', 'batteryKw', 'gridKw', 'servedLoadKw'];
  const missing = required.filter(key => !finite(input?.[key]));
  if (input?.auxiliaryIncludedInLoad !== true && input?.auxiliaryExcluded === true) {
    input = { ...input, auxiliaryKw: 0 };
  }
  if (!finite(input?.auxiliaryKw)) missing.push('auxiliaryKw');
  if (missing.length > 0 || (input?.alignmentStatus && input.alignmentStatus !== 'aligned')) {
    const reasons = missing.map(key => `missing:${key}`);
    if (input?.alignmentStatus && input.alignmentStatus !== 'aligned') reasons.push('inputsNotAligned');
    return {
      ...unavailable('kW', reasons), residualKw: null, residualPct: null,
      normalizationKw: null, toleranceKw: null, balanceWithinTolerance: null
    };
  }
  const { generationKw: g, batteryKw: b, gridKw: p, servedLoadKw: l, auxiliaryKw: a } = input;
  const residualKw = g + b + p - l - a;
  const sourcesKw = g + Math.max(b, 0) + Math.max(p, 0);
  const sinksKw = l + a + Math.max(-b, 0) + Math.max(-p, 0);
  const normalizationKw = Math.max(sourcesKw, sinksKw, 1);
  const toleranceKw = Math.max(0.5, 0.01 * normalizationKw);
  return {
    value: round(residualKw), unit: 'kW', status: 'complete', reasonCodes: [],
    residualKw: round(residualKw), residualPct: round(100 * residualKw / normalizationKw),
    normalizationKw: round(normalizationKw), toleranceKw: round(toleranceKw),
    balanceWithinTolerance: Math.abs(residualKw) <= toleranceKw,
    sourcesKw: round(sourcesKw), sinksKw: round(sinksKw)
  };
}

function calculateLoadConsistency(requestedKw, servedKw, unservedKw) {
  if (![requestedKw, servedKw, unservedKw].every(finite)) {
    return { ...unavailable('kW', 'missingLoadInput'), residualKw: null };
  }
  const residualKw = requestedKw - servedKw - unservedKw;
  return { ...result(round(residualKw), 'kW'), residualKw: round(residualKw) };
}

function calculateFeederMetrics(feeders, siteServedKw, configuredFeederIds = null) {
  if (!finite(siteServedKw)) return { ...unavailable('kW', 'missingSiteServedLoad'), feederResidualKw: null };
  const byId = new Map((Array.isArray(feeders) ? feeders : []).map(item => [item.feederId, item]));
  const configurationKnown = Array.isArray(configuredFeederIds);
  const ids = configurationKnown ? configuredFeederIds : [...byId.keys()];
  const missing = ids.filter(id => !finite(byId.get(id)?.servedPowerKw));
  const reporting = ids.length - missing.length;
  const channels = ids.map(id => {
    const item = byId.get(id);
    const served = finite(item?.servedPowerKw) ? item.servedPowerKw : null;
    return {
      feederId: id, servedPowerKw: served,
      sharePct: siteServedKw === 0 ? null : (served === null ? null : round(100 * served / siteServedKw)),
      shareStatus: siteServedKw === 0 ? 'notApplicable' : (served === null ? 'unavailable' : 'complete'),
      state: item?.state ?? null
    };
  });
  if (!configurationKnown || missing.length) {
    return {
      ...unavailable('kW', !configurationKnown ? ['configuredFeederSetUnavailable'] :
        missing.map(id => `missingFeeder:${id}`)), feederResidualKw: null,
      configuredChannelCount: configurationKnown ? ids.length : null,
      reportingChannelCount: byId.size,
      energizedChannelCount: channels.filter(item => item.state === 'served').length, feeders: channels
    };
  }
  const feederResidualKw = channels.reduce((sum, item) => sum + item.servedPowerKw, 0) - siteServedKw;
  return {
    ...result(round(feederResidualKw), 'kW'), feederResidualKw: round(feederResidualKw),
    configuredChannelCount: ids.length, reportingChannelCount: reporting,
    energizedChannelCount: channels.filter(item => item.state === 'served').length, feeders: channels
  };
}

function calculateElectricalCompatibility(input) {
  if (input?.gridAvailable === false) {
    return {
      status: 'notApplicable', reasonCodes: ['gridUnavailable'], voltageDifferenceV: null,
      voltageDifferencePct: null, frequencyDifferenceHz: null,
      voltageFrequencyCompatible: null, electricalSynchronization: 'notApplicable'
    };
  }
  if (input?.gridAvailable !== true) {
    return {
      status: 'unavailable', reasonCodes: ['missing:gridAvailable'], voltageDifferenceV: null,
      voltageDifferencePct: null, frequencyDifferenceHz: null,
      voltageFrequencyCompatible: null, electricalSynchronization: 'unknown'
    };
  }
  const required = ['utilityVoltageV', 'siteVoltageV', 'utilityFrequencyHz', 'siteFrequencyHz'];
  const missing = required.filter(key => !finite(input?.[key]));
  if (missing.length || !input?.utilityVoltageBasis || input.utilityVoltageBasis !== input.siteVoltageBasis) {
    return {
      status: 'unavailable', reasonCodes: missing.length ? missing.map(key => `missing:${key}`) : ['voltageBasisMismatch'],
      voltageDifferenceV: null, voltageDifferencePct: null, frequencyDifferenceHz: null,
      voltageFrequencyCompatible: null, electricalSynchronization: 'unknown'
    };
  }
  const voltageDifferenceV = input.siteVoltageV - input.utilityVoltageV;
  const frequencyDifferenceHz = input.siteFrequencyHz - input.utilityFrequencyHz;
  const voltageDifferencePct = input.utilityVoltageV === 0 ? null :
    100 * Math.abs(voltageDifferenceV) / Math.abs(input.utilityVoltageV);
  const busesEnergized = input.utilityVoltageV > 0 && input.siteVoltageV > 0;
  const compatible = voltageDifferencePct === null ? null :
    busesEnergized && voltageDifferencePct <= 5 && Math.abs(frequencyDifferenceHz) <= 0.1;
  let synchronization = 'unknown';
  if (input.trustedSynchronization === true) synchronization = 'synchronized';
  else if (input.trustedSynchronization === false) synchronization = 'notSynchronized';
  else if (input.phaseComparisonComplete === true) synchronization = compatible ? 'synchronized' : 'notSynchronized';
  return {
    status: 'complete', reasonCodes: [], voltageDifferenceV: round(voltageDifferenceV),
    voltageDifferencePct: voltageDifferencePct === null ? null : round(voltageDifferencePct),
    frequencyDifferenceHz: round(frequencyDifferenceHz), voltageFrequencyCompatible: compatible,
    electricalSynchronization: synchronization
  };
}

function interpolate(a, b, fraction) {
  return a + (b - a) * fraction;
}

/**
 * Integrate power without carrying values across gaps. Segments are clipped to
 * range boundaries and split at zero so directional import/export or
 * charge/discharge energy never cancel each other.
 */
function integratePowerSamples(samples, start, end, options = {}) {
  const startMs = toTime(start);
  const endMs = toTime(end);
  if (startMs === null || endMs === null || endMs <= startMs) throw new TypeError('A valid integration range is required');
  const maxGapMs = (options.maxGapSeconds ?? DEFAULT_MAX_SEGMENT_SECONDS) * 1000;
  const ordered = (samples || [])
    .map(sample => ({ time: toTime(sample.sampleTime ?? sample.observedAt), kw: Number(sample.kw ?? sample.value) }))
    .filter(sample => sample.time !== null && finite(sample.kw))
    .sort((a, b) => a.time - b.time);
  let signedKwh = 0;
  let positiveKwh = 0;
  let negativeKwh = 0;
  let coveredMs = 0;
  let segmentCount = 0;
  for (let index = 1; index < ordered.length; index += 1) {
    const left = ordered[index - 1];
    const right = ordered[index];
    const originalDuration = right.time - left.time;
    if (originalDuration <= 0 || originalDuration > maxGapMs) continue;
    const from = Math.max(left.time, startMs);
    const to = Math.min(right.time, endMs);
    if (to <= from) continue;
    const fromKw = interpolate(left.kw, right.kw, (from - left.time) / originalDuration);
    const toKw = interpolate(left.kw, right.kw, (to - left.time) / originalDuration);
    const pieces = [{ from, to, fromKw, toKw }];
    if ((fromKw < 0 && toKw > 0) || (fromKw > 0 && toKw < 0)) {
      const zeroAt = from + (to - from) * Math.abs(fromKw) / (Math.abs(fromKw) + Math.abs(toKw));
      pieces.splice(0, 1,
        { from, to: zeroAt, fromKw, toKw: 0 },
        { from: zeroAt, to, fromKw: 0, toKw });
    }
    for (const piece of pieces) {
      const hours = (piece.to - piece.from) / 3600000;
      const energy = (piece.fromKw + piece.toKw) / 2 * hours;
      signedKwh += energy;
      if (energy >= 0) positiveKwh += energy;
      else negativeKwh += -energy;
      coveredMs += piece.to - piece.from;
      segmentCount += 1;
    }
  }
  const c = coverage((endMs - startMs) / 1000, coveredMs / 1000, ordered.length);
  return {
    signedEnergyKwh: round(signedKwh), positiveEnergyKwh: round(positiveKwh),
    negativeEnergyKwh: round(negativeKwh), segmentCount, ...c,
    status: c.coveredSeconds === c.expectedSeconds ? 'complete' : (c.coveredSeconds > 0 ? 'partial' : 'unavailable'),
    reasonCodes: c.coveredSeconds === c.expectedSeconds ? [] : ['incompleteCoverage']
  };
}

function calculateDemand(integration, intervalSeconds, targetKw = null) {
  const complete = integration && integration.status === 'complete' &&
    Number(integration.coveredSeconds) === Number(intervalSeconds);
  const coveredMeanKw = integration?.coveredSeconds > 0 ?
    round(integration.positiveEnergyKwh / (integration.coveredSeconds / 3600)) : null;
  if (!complete) {
    return {
      ...result(null, 'kW', integration?.coveredSeconds > 0 ? 'partial' : 'unavailable', ['incompleteCoverage']),
      intervalDemandKw: null, coveredImportEnergyKwh: integration?.positiveEnergyKwh ?? null,
      coveredTimeMeanKw: coveredMeanKw, marginKw: null, excessKw: null
    };
  }
  const intervalDemandKw = integration.positiveEnergyKwh / (intervalSeconds / 3600);
  const marginKw = finite(targetKw) ? targetKw - intervalDemandKw : null;
  return {
    ...result(round(intervalDemandKw), 'kW'), intervalDemandKw: round(intervalDemandKw),
    coveredImportEnergyKwh: integration.positiveEnergyKwh, coveredTimeMeanKw: coveredMeanKw,
    marginKw: marginKw === null ? null : round(marginKw),
    excessKw: marginKw === null ? null : round(Math.max(-marginKw, 0))
  };
}

function calculatePeakDemand(intervals) {
  const completed = (intervals || []).filter(interval =>
    interval && interval.status === 'complete' && finite(interval.intervalDemandKw));
  const partial = (intervals || []).some(interval => !interval || interval.status !== 'complete');
  if (!completed.length) {
    return {
      ...unavailable('kW', partial ? 'noCompleteDemandIntervals' : 'noDemandIntervals'),
      peakKw: null, intervalStart: null, intervalEnd: null, method: null, coverage: null
    };
  }
  const peak = completed.reduce((best, current) =>
    current.intervalDemandKw > best.intervalDemandKw ? current : best);
  return {
    ...result(round(peak.intervalDemandKw), 'kW', partial ? 'partial' : 'complete',
      partial ? ['periodContainsIncompleteIntervals'] : []),
    peakKw: round(peak.intervalDemandKw), intervalStart: peak.intervalStart || null,
    intervalEnd: peak.intervalEnd || null, method: peak.method || 'sampledPowerEstimate',
    coverage: peak.coverage || null
  };
}

// Exact decimal subtraction without binary floating-point loss.
function decimalDelta(previous, current) {
  const parse = value => {
    const text = String(value);
    if (!/^-?\d+(\.\d+)?$/.test(text)) throw new TypeError('Counter values must be decimal strings or numbers');
    const negative = text.startsWith('-');
    const unsigned = negative ? text.slice(1) : text;
    const [whole, fraction = ''] = unsigned.split('.');
    return { negative, whole, fraction };
  };
  const a = parse(previous);
  const b = parse(current);
  const scale = Math.max(a.fraction.length, b.fraction.length);
  const integer = item => {
    const magnitude = BigInt(item.whole + item.fraction.padEnd(scale, '0'));
    return item.negative ? -magnitude : magnitude;
  };
  const diff = integer(b) - integer(a);
  const negative = diff < 0n;
  const digits = (negative ? -diff : diff).toString().padStart(scale + 1, '0');
  const text = scale === 0 ? digits : `${digits.slice(0, -scale)}.${digits.slice(-scale)}`;
  return `${negative ? '-' : ''}${text}`;
}

function calculateCounterDelta(previous, current, options = {}) {
  if (previous === null || previous === undefined || current === null || current === undefined) {
    return { ...unavailable(options.unit || 'kWh', 'missingCounterEndpoint'), delta: null };
  }
  if (options.replacement || options.continuity === false) {
    return { ...unavailable(options.unit || 'kWh', options.replacement ? 'counterReplacement' : 'counterContinuityUnknown'), delta: null };
  }
  const delta = decimalDelta(previous, current);
  if (delta.startsWith('-')) {
    return { ...unavailable(options.unit || 'kWh', options.rollover ? 'counterRolloverUnresolved' : 'counterReset'), delta: null };
  }
  return { ...result(delta, options.unit || 'kWh'), delta };
}

function calculateBessEnergy(input) {
  const capacity = finite(input?.explicitFullChargeCapacityKwh) ? input.explicitFullChargeCapacityKwh :
    (finite(input?.nameplateCapacityKwh) && finite(input?.sohPct) ? input.nameplateCapacityKwh * input.sohPct / 100 : null);
  if (!finite(capacity) || !finite(input?.socPct) || !finite(input?.minimumSocPct) || !finite(input?.maximumSocPct)) {
    return {
      status: finite(input?.directStoredEnergyKwh) ? 'partial' : 'unavailable',
      reasonCodes: ['missingCapacityOrSocConfiguration'],
      reportedStoredEnergyKwh: finite(input?.directStoredEnergyKwh) ? input.directStoredEnergyKwh : null,
      estimatedStoredEnergyKwh: null, reserveEnergyKwh: null,
      maximumStoredEnergyKwh: null, operatingWindowCapacityKwh: null,
      usableEnergyAboveReserveKwh: null, chargeHeadroomKwh: null,
      estimatedDeliverableAcEnergyKwh: null
    };
  }
  const estimatedStored = capacity * input.socPct / 100;
  const stored = finite(input.directStoredEnergyKwh) ? input.directStoredEnergyKwh : estimatedStored;
  const reserve = capacity * input.minimumSocPct / 100;
  const maximum = capacity * input.maximumSocPct / 100;
  return {
    status: 'complete', reasonCodes: [], fullChargeCapacityKwh: round(capacity),
    capacitySource: finite(input.explicitFullChargeCapacityKwh) ? 'deviceDefined' : 'nameplateSohEstimate',
    reportedStoredEnergyKwh: finite(input.directStoredEnergyKwh) ? input.directStoredEnergyKwh : null,
    estimatedStoredEnergyKwh: round(estimatedStored), storedEnergyUsedKwh: round(stored),
    storedEnergySource: finite(input.directStoredEnergyKwh) ? 'directReported' : 'socEstimate',
    reserveEnergyKwh: round(reserve), maximumStoredEnergyKwh: round(maximum),
    operatingWindowCapacityKwh: round(capacity * (input.maximumSocPct - input.minimumSocPct) / 100),
    usableEnergyAboveReserveKwh: round(Math.max(stored - reserve, 0)),
    chargeHeadroomKwh: round(Math.max(maximum - stored, 0)),
    estimatedDeliverableAcEnergyKwh: finite(input.dischargeEfficiency) ?
      round(Math.max(stored - reserve, 0) * input.dischargeEfficiency) : null
  };
}

function calculateBessEfficiency(input) {
  const cin = input?.chargeKwh;
  const dout = input?.dischargeKwh;
  const configuredRte = finite(input?.chargeEfficiency) && finite(input?.dischargeEfficiency) ?
    round(100 * input.chargeEfficiency * input.dischargeEfficiency) : null;
  const ratio = finite(cin) && cin > 0 && finite(dout) ? round(100 * dout / cin) : null;
  const tolerance = finite(input?.nameplateCapacityKwh) ? input.nameplateCapacityKwh * 0.001 : null;
  const endpointDifference = finite(input?.startStoredEnergyKwh) && finite(input?.endStoredEnergyKwh) ?
    input.endStoredEnergyKwh - input.startStoredEnergyKwh : null;
  const matched = finite(cin) && cin > 0 && finite(dout) && finite(endpointDifference) && finite(tolerance) &&
    Math.abs(endpointDifference) <= tolerance && input?.physicalUnitUnchanged === true && input?.configurationUnchanged === true;
  return {
    status: finite(cin) && finite(dout) ? 'complete' : 'unavailable',
    reasonCodes: finite(cin) && finite(dout) ? [] : ['missingThroughput'],
    acThroughputKwh: finite(cin) && finite(dout) ? round(cin + dout) : null,
    dischargeToChargeRatioPct: ratio,
    configuredRoundTripEfficiencyPct: configuredRte,
    observedAcRoundTripEfficiencyPct: matched ? ratio : null,
    observedAcRoundTripEfficiencyStatus: matched ? 'complete' : 'unavailable',
    observedAcRoundTripEfficiencyReasonCodes: matched ? [] : ['unmatchedEnergyEndpoints'],
    storedEnergyEndpointDifferenceKwh: endpointDifference === null ? null : round(endpointDifference),
    storedEnergyEndpointToleranceKwh: tolerance === null ? null : round(tolerance),
    acDischargeEquivalentCycles: finite(dout) && finite(input?.referenceCapacityKwh) && input.referenceCapacityKwh > 0 ?
      round(dout / input.referenceCapacityKwh) : null,
    observedDirectionalEfficiencyPct: null,
    observedDirectionalEfficiency: unavailable('%', 'matchingAcDcBoundaryInputsUnavailable'),
    wholeSystemRoundTripEfficiencyPct: null,
    wholeSystemRoundTripEfficiency: unavailable('%', 'auxiliaryEnergyAllocationUnavailable')
  };
}

function calculateBessStoredEnergyBalance(input) {
  const required = ['startStoredEnergyKwh', 'endStoredEnergyKwh', 'chargeKwh',
    'dischargeKwh', 'chargeEfficiency', 'dischargeEfficiency'];
  const missing = required.filter(key => !finite(input?.[key]));
  if (missing.length || input.dischargeEfficiency <= 0) {
    return { ...unavailable('kWh', missing.map(key => `missing:${key}`)), modeledChangeKwh: null, residualKwh: null };
  }
  if (input.internalAuxKwh === undefined || input.internalAuxKwh === null) {
    return { ...unavailable('kWh', 'internalAuxiliaryBoundaryUnavailable'), modeledChangeKwh: null, residualKwh: null };
  }
  const modeledChange = input.chargeEfficiency * input.chargeKwh -
    input.dischargeKwh / input.dischargeEfficiency - input.internalAuxKwh;
  const observedChange = input.endStoredEnergyKwh - input.startStoredEnergyKwh;
  return {
    ...result(round(observedChange - modeledChange), 'kWh'), observedChangeKwh: round(observedChange),
    modeledChangeKwh: round(modeledChange), residualKwh: round(observedChange - modeledChange)
  };
}

function calculateInverterPerformance(input) {
  const conversion = finite(input?.acOutputEnergyKwh) && finite(input?.dcInputEnergyKwh) && input.dcInputEnergyKwh > 0
    ? result(round(100 * input.acOutputEnergyKwh / input.dcInputEnergyKwh), '%', 'complete', [], {
      source: input.dcInputSource === 'mpptVoltageCurrent' ? 'estimatedDcInput' : 'measuredBoundaryEnergy'
    })
    : unavailable('%', 'matchingAcDcEnergyUnavailable');
  const utilization = finite(input?.powerKw) && finite(input?.ratedPowerKw) && input.ratedPowerKw > 0
    ? result(round(100 * input.powerKw / input.ratedPowerKw), '%')
    : unavailable('%', 'ratedPowerUnavailable');
  return { conversionEfficiency: conversion, capacityUtilization: utilization };
}

function calculateEnergyReconciliation(input) {
  const keys = ['generationKwh', 'loadKwh', 'bessChargeKwh', 'bessDischargeKwh', 'gridImportKwh', 'gridExportKwh'];
  if (input?.auxiliaryRequired === true) keys.push('auxiliaryKwh');
  const missing = keys.filter(key => !finite(input?.[key]));
  if (missing.length) {
    return {
      status: 'unavailable', reasonCodes: missing.map(key => `missing:${key}`),
      residualKwh: null, residualPct: null, toleranceKwh: null, balanceWithinTolerance: null
    };
  }
  const auxiliaryKwh = input.auxiliaryRequired === true ? input.auxiliaryKwh : 0;
  const residual = input.generationKwh + input.bessDischargeKwh + input.gridImportKwh -
    input.loadKwh - auxiliaryKwh - input.bessChargeKwh - input.gridExportKwh;
  const sources = input.generationKwh + input.bessDischargeKwh + input.gridImportKwh;
  const sinks = input.loadKwh + auxiliaryKwh + input.bessChargeKwh + input.gridExportKwh;
  const normalization = Math.max(sources, sinks, 1);
  const tolerance = Math.max(0.5, 0.01 * normalization);
  return {
    status: 'complete', reasonCodes: [], residualKwh: round(residual),
    residualPct: round(100 * residual / normalization), toleranceKwh: round(tolerance),
    normalizationKwh: round(normalization), balanceWithinTolerance: Math.abs(residual) <= tolerance
  };
}

export {
  CALCULATION_VERSION,
  DEFAULT_MAX_AGE_SECONDS,
  DEFAULT_MAX_SEGMENT_SECONDS,
  DEFAULT_MAX_SKEW_SECONDS,
  alignObservations,
  calculateActivePowerBalance,
  calculateBessEfficiency,
  calculateBessEnergy,
  calculateBessStoredEnergyBalance,
  calculateCounterDelta,
  calculateDemand,
  calculatePeakDemand,
  calculateElectricalCompatibility,
  calculateEnergyReconciliation,
  calculateFeederMetrics,
  calculateInverterPerformance,
  calculateLoadConsistency,
  coverage,
  decimalDelta,
  integratePowerSamples,
  notApplicable,
  result,
  round,
  unavailable
};
